import { useState, useEffect, useCallback, useRef } from 'react';

const STATUSES = ['ALL', 'N', 'S', 'Y', 'R', 'OUTSOURCE'];

/**
 * Per-status totals for a sample worklist, computed by the same search endpoint and
 * filters as the list itself (pageSize=1, totalCount only), so a status card always
 * equals what that status tab shows. A failed status resolves to null (rendered "—").
 *
 * @param {Function} searchFn     service search method, e.g. sampleCollectionService.searchSamples
 * @param {Object}   filterParams list filters excluding status / paging
 * @param {*}        refreshKey   change to force a reload (e.g. after collect/receive)
 */
export default function useSampleStatusCounts(searchFn, filterParams, refreshKey) {
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const requestId = useRef(0);
  const paramsKey = JSON.stringify(filterParams || {});

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    const params = JSON.parse(paramsKey);
    const results = await Promise.allSettled(
      STATUSES.map((status) => searchFn({ ...params, status, pageNumber: 1, pageSize: 1 }))
    );
    if (id !== requestId.current) return; // a newer request superseded this one
    const next = {};
    results.forEach((r, i) => {
      next[STATUSES[i]] =
        r.status === 'fulfilled' && r.value?.success ? r.value.data?.totalCount ?? 0 : null;
    });
    setCounts(next);
    setLoading(false);
  }, [searchFn, paramsKey]);

  useEffect(() => {
    const timer = setTimeout(load, 350);
    return () => clearTimeout(timer);
  }, [load, refreshKey]);

  return { counts, loading, reload: load };
}
